/**
 * Ligne de liste commune (retour du propriétaire, 09/10/2026) : demandes,
 * devis, factures, propositions, contrats et commandes avaient chacun leur
 * présentation. Une seule grammaire désormais, lue de gauche à droite :
 * visuel · qui et quoi · prochaine étape · montant · état · chevron.
 * Les lignes d'une RecordList forment un groupe dans une même carte.
 *
 * `className` n'ajoute qu'un repère (tests, mise en avant) ; l'apparence vient
 * toujours de `.record-row` (components/ui/record.css).
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { ActionMenu, type ActionMenuItem } from './action-menu.js';

export function RecordList(props: { label: string; children: ReactNode }): ReactNode {
  return <ul className="record-list" aria-label={props.label}>{props.children}</ul>;
}

export function RecordRow(props: {
  /** Destination : toute la ligne est un lien. */
  to?: string;
  state?: unknown;
  /** Sans destination : ouverture par bouton (la ligne peut alors porter des actions). */
  onOpen?: () => void;
  lead: ReactNode;
  title: ReactNode;
  /** Lignes de contexte, de la plus utile à la moins utile. */
  lines?: ReactNode[];
  /** Chaque ligne de contexte tient sur une ligne, coupée par « … » (listes denses de l'accueil). */
  singleLine?: boolean;
  /** Ce qui attend ce dossier, en une phrase. */
  next?: string | undefined;
  /** `struck` : montant qui ne compte plus (paiement annulé), barré et atténué. */
  amount?: { value: string; label?: ReactNode; struck?: boolean } | undefined;
  badge?: ReactNode;
  urgent?: boolean;
  /** Actions rares de la ligne, dans son menu « … » (à la place du chevron). */
  menu?: ActionMenuItem[];
  /** Nom lu du bouton « … » (« Déplacer Technicien ») ; « Plus d'actions » sinon. */
  menuLabel?: string;
  /**
   * Un seul geste fréquent, en bouton icône de 44 px au bout de la ligne
   * (appeler un contact), à la place du chevron. Avec un `menu`, le geste
   * fréquent reste visible et le « … » le suit (appeler, puis supprimer).
   */
  trailing?: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** Repère « vous êtes ici » ou « jour courant », porté par le lien de la ligne. */
  ariaCurrent?: 'location' | 'date' | 'page' | undefined;
}): ReactNode {
  const hasMenu = Boolean(props.menu?.length);
  const interactive = hasMenu || Boolean(props.actions) || Boolean(props.trailing);
  const classes = [
    'record-row', props.urgent && 'record-row--urgent', interactive && 'record-row--actions',
    props.singleLine && 'record-row--single-line',
    props.className,
  ].filter(Boolean).join(' ');
  const titleText = <strong className="record-row__title">{props.title}</strong>;
  // Avec des actions, seul le titre ouvre la fiche ; sa zone s'étend à la
  // ligne, et les actions restent au-dessus (un bouton dans un lien est interdit).
  const title = interactive && props.to ? (
    <Link className="record-row__open" to={props.to} state={props.state} aria-current={props.ariaCurrent}>{titleText}</Link>
  ) : props.onOpen ? (
    <button type="button" className="record-row__open" onClick={props.onOpen}>{titleText}</button>
  ) : titleText;
  const content = (
    <>
      <span className="record-row__lead" aria-hidden="true">{props.lead}</span>
      <span className="record-row__main">
        {title}
        {(props.lines ?? []).filter(Boolean).map((line, index) => (
          <span key={index} className="record-row__line">{line}</span>
        ))}
        {props.next ? <span className="record-row__next">{props.next}</span> : null}
      </span>
      {props.amount ? (
        <span className={props.amount.struck ? 'record-row__amount record-row__amount--struck' : 'record-row__amount'}>
          <strong>{props.amount.value}</strong>
          {props.amount.label ? <small>{props.amount.label}</small> : null}
        </span>
      ) : null}
      {props.badge}
      {hasMenu ? (
        <span className={props.trailing ? 'record-row__menu record-row__menu--with-trailing' : 'record-row__menu'}>
          {props.trailing}
          <ActionMenu items={props.menu ?? []} {...(props.menuLabel ? { label: props.menuLabel } : {})} />
        </span>
      ) : props.trailing ? (
        <span className="record-row__trailing">{props.trailing}</span>
      ) : props.to || props.onOpen ? (
        // Pas de chevron sur une ligne qui ne mène nulle part : il promettrait une ouverture.
        <ChevronRight className="record-row__chevron" size={18} aria-hidden="true" />
      ) : null}
      {props.actions ? <span className="record-row__actions">{props.actions}</span> : null}
    </>
  );
  return props.to && !interactive
    ? <Link className={classes} to={props.to} state={props.state} aria-current={props.ariaCurrent}>{content}</Link>
    : <div className={classes}>{content}</div>;
}

/** Visuel d'une ligne qui ne désigne pas un client : pictogramme dans une pastille. */
export function RecordIcon(props: { children: ReactNode }): ReactNode {
  return <span className="record-row__icon">{props.children}</span>;
}
